import { useMemo } from 'react';
import { BRAND_BY_DOMAIN, DEFAULT_BRAND_KEY } from '../config/brands';
import { TENANT_BRANDING_CACHE_PREFIX } from '../api/publicApi';
import {
  DARK_AD_THEMES_ENABLED,
  isNearWhite,
  resolveAdThemes,
  type AdTheme,
} from '../config/adThemes';
import type { ColorPreset } from '../components/ColorPicker';

// Mirrors the *.localhost aliases in useTenantBranding so local dev resolves
// the same brand as the deployed host.
const LOCAL_BRAND_KEYS: Record<string, string> = {
  capcana: 'capcana',
  cityplace: 'cityplace',
  zabelabeach: 'zabelabeach',
  canabay: 'canabay',
};

const currentBrandKey = (): string => {
  const host = window.location.hostname.toLowerCase();
  if (BRAND_BY_DOMAIN[host]) return BRAND_BY_DOMAIN[host].key;
  if (host.endsWith('.localhost') || host.endsWith('.local')) {
    return LOCAL_BRAND_KEYS[host.split('.')[0]] ?? DEFAULT_BRAND_KEY;
  }
  return DEFAULT_BRAND_KEY;
};

// useTenantBranding writes the tenant's primary color to --tenant-primary;
// reading it back keeps this hook free of a second tenant fetch.
const currentTenantPrimary = (): string | null => {
  const v = getComputedStyle(document.documentElement).getPropertyValue('--tenant-primary').trim();
  return v || null;
};

// Optional backend palette. useTenantBranding caches the tenant under
// `${TENANT_BRANDING_CACHE_PREFIX}<domain>`; if a tenant defines
// settings.adThemes there, it wins over the static config.
const currentTenantOverride = (): unknown => {
  try {
    const host = window.location.hostname.toLowerCase();
    const domain = host.split('.').slice(-2).join('.');
    const raw = sessionStorage.getItem(`${TENANT_BRANDING_CACHE_PREFIX}${domain}`);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed?.settings?.adThemes ?? parsed?.adThemes;
  } catch {
    return undefined;
  }
};

export interface UseAdThemesResult {
  themes: AdTheme[];
  /** Ready for `<ColorPicker presets>`; Dark themes disabled until SAM-432, white and near-white never offered (SAM-1105). */
  presets: ColorPreset[];
}

export const useAdThemes = (): UseAdThemesResult => {
  return useMemo(() => {
    const themes = resolveAdThemes({
      brandKey: currentBrandKey(),
      tenantPrimary: currentTenantPrimary(),
      tenantOverride: currentTenantOverride(),
    });
    const presets: ColorPreset[] = themes.map((t) => {
      const dark = t.mode === 'dark' && !DARK_AD_THEMES_ENABLED;
      // SAM-1105: white and near-white are not offered for a Featured card.
      const white = isNearWhite(t.hex);
      return {
        hex: t.hex,
        name: t.name,
        disabled: dark || white,
        hint: dark
          ? `${t.name} — unavailable until SAM-432 (app renders black text)`
          : white
            ? `${t.name} — white looks washed out in the app`
            : t.name,
      };
    });
    return { themes, presets };
  }, []);
};
